import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { SharedEditableInputComponent } from '../shared-input.component';
import { FeedbackService } from '../../../../../core/feedback.service';
import { isStringAnUrl } from '../../../../../core/utilities';
import { EditLocationService } from '../../service/edit-location.service';
import { takeUntil } from 'rxjs/operators';
import { AbstractControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LatitudeValidators, LongitudeValidators } from '../../service/validators';
import { Subscription } from 'rxjs';
import { EditLocationLocalStore } from '../../edit-location-local-store.service';
import { TranslateModule } from '@ngx-translate/core';
import { MatTooltip } from '@angular/material/tooltip';
import { NgStyle, NgClass } from '@angular/common';
import { GtagEventDirective } from '../../../../../core/gx-gtag/gtag-event.directive';
import { MatIconAnchor, MatIconButton } from '@angular/material/button';
import { DblclickPrependTodayDirective } from '../../../../../directives/dblclick-prepend-today.directive';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { GxInputDirective } from '../../../../../directives/gxinput.directive';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';

// Heavily inspired by `TextualColumnComponent`
@Component({
    selector: 'app-editable-textual-input',
    templateUrl: './editable-textual-input.component.html',
    styleUrls: ['./editable-textual-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatIcon,
        MatFormField,
        MatLabel,
        MatInput,
        GxInputDirective,
        MatError,
        CdkTextareaAutosize,
        DblclickPrependTodayDirective,
        MatIconAnchor,
        GtagEventDirective,
        NgStyle,
        MatIconButton,
        MatTooltip,
        NgClass,
        TranslateModule,
    ],
})
export class EditableTextualInputComponent extends SharedEditableInputComponent implements OnInit {

  @Input() htmlType: 'text' | 'number' = 'text';

  @Input() subtype:
      | 'text'
      | 'comment' // with double-click functionality
      | 'multiline_text' // for e.g. special hours, from-the-business etc
      | 'link' // w/ a "preview" suffix
      = 'text';

  coordinateListenerSubscription: Subscription;

  constructor(protected cd: ChangeDetectorRef,
              protected feedbackService: FeedbackService,
              protected sidePanelService: EditLocationService,
              protected localStore: EditLocationLocalStore) {
    super(cd, feedbackService, sidePanelService, localStore);
  }

  ngOnInit(): void {
    super.ngOnInit();
    // check for new formGroup and nested controls
    if (this.localStore.formGroup && this.formGroupNameRef) {
      this.coordinateListenerSubscription = this.registerCoordinatesListener();
    }

    // listener needs to be registered anew if changes were canceled or if a new location was selected
    this.localStore.newFormGroupNotifier$.pipe(takeUntil(this.componentDestroyed))
        .subscribe(() => {
          if (this.localStore.formGroup && this.formGroupNameRef) {
            this.coordinateListenerSubscription.unsubscribe();
            this.coordinateListenerSubscription = this.registerCoordinatesListener();
          }
        });
  }


  /**
   * Wrapper around the protected `actualRawInput`.
   */
  getValue(): string {
    return this.actualRawInput?.value;
  }

  /**
   * For rendering an a-href in the subtype:link component.
   */
  isValueAnUrl(): boolean {
    return isStringAnUrl(this.actualRawInput?.value || '');
  }

  /**
   * Go to the link present in the field's value, if any.
   * Used in components of subtype `link`.
   */
  goToLink() {
    // double check
    if (!this.isValueAnUrl()) {
      return;
    }
    const val = this.getValue();
    const link = val.startsWith('http') ? val : `http://${val}`;
    window.open(link, '_blank');
  }

  /**
   * Listens to Imported coordinates changes and sets validators dynamically + makes sure that
   * the decimal separator is always '.'
   * @private
   */
  private registerCoordinatesListener() {
    return this.localStore.formGroup?.controls[this.formGroupNameRef]['controls'][this.fieldId].valueChanges
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe(val => {
          const latitudeControl = this.localStore.formGroup?.controls[this.formGroupNameRef]['controls']['latitude'] as AbstractControl;
          const longitudeControl = this.localStore.formGroup?.controls[this.formGroupNameRef]['controls']['longitude'] as AbstractControl;
          if (this.fieldId === 'latitude') {
            // force '.' as the decimal separator
            // react only to user changes!
            latitudeControl.setValue(val?.replace(/,/g, '.'), { emitEvent: false });
            // if the paired control has a value, set a 'required' validator as well the default ones
            longitudeControl.setValidators(LongitudeValidators.concat(val ? Validators.required : []));
            // update the validity of the paired control
            longitudeControl.updateValueAndValidity({ emitEvent: false });
          } else if (this.fieldId === 'longitude') {
            longitudeControl.setValue(val?.replace(/,/g, '.'), { emitEvent: false });
            latitudeControl.setValidators(LatitudeValidators.concat(val ? Validators.required : []));
            latitudeControl.updateValueAndValidity({ emitEvent: false });
          }
        });
  }

}
